'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useState } from 'react'

import { TeamAvatar } from '@components/ui/avatar'
import * as DropdownMenu from '@components/ui/dropdown-menu'
import IconAdd from '@images/icons/plus.svg'
import IconCheckmark from '@images/icons/check.svg'
import { cn } from '@lib/cn'
import { useAppRoute } from '@lib/use-app-route'
import { useMe } from '@resolvers/auth'

import { AccountMenuItems } from './user-menu'

const TeamMenu = ({ size = 'md' }: { size?: 'md' | 'lg' }) => {
  const router = useRouter()
  const { team, teamSlug, envKind, billing } = useAppRoute()
  const { me } = useMe()
  const [open, setOpen] = useState(false)

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Switch team"
          className={cn(
            'flex min-w-0 items-center gap-2.5 outline-hidden',
            size === 'md' && 'h-10 w-full rounded-lg px-2 hover:bg-o-chip data-[state=open]:bg-o-chip',
            size === 'lg' && 'rounded-xl border border-o-line-strong bg-o-card py-1.5 ps-1.5 pe-3'
          )}
        >
          <TeamAvatar name={team?.name} id={team?.id} image={team?.avatar} size={28} priority className={size === 'lg' ? 'rounded-lg' : 'rounded-[7px]'} />
          <span
            className={cn(
              'truncate text-start text-o-ink',
              size === 'md' && 'flex-1 text-sm font-medium',
              size === 'lg' && 'text-base font-semibold'
            )}
          >
            {team?.name ?? 'Team'}
          </span>
          <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden className="shrink-0 text-o-muted">
            <path d="M3.5 4.5L6 2L8.5 4.5M3.5 7.5L6 10L8.5 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="start"
        className={cn(
          'rounded-xl bg-o-card',
          size === 'md' && 'w-[228px] text-[13px]',
          size === 'lg' && 'w-[300px] max-w-[calc(100vw-2rem)] text-base [&_[role^=menuitem]]:px-3 [&_[role^=menuitem]]:py-[11px]'
        )}
      >
        {size === 'md' && (
          <div className="border-b border-o-line px-4 py-3">
            <p className="truncate font-medium text-o-ink">{team?.name}</p>
            <p className="truncate text-o-muted">app.orbitid.com/{teamSlug}</p>
          </div>
        )}
        <div className="py-1">
          <DropdownMenu.Item asChild>
            <Link href={`/${teamSlug}/team`}>Team settings</Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link href={`/${teamSlug}/team/members`}>Members</Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link href={`/${teamSlug}/team/billing`}>{billing?.active ? 'Billing' : 'Upgrade'}</Link>
          </DropdownMenu.Item>
        </div>
        {me && me.teams.length > 0 && (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Label>Switch team</DropdownMenu.Label>
            {me.teams.map((candidate) => (
              <DropdownMenu.Item
                key={candidate.id}
                onSelect={() => router.push(`/${candidate.slug}/${envKind}`)}
                icon={
                  candidate.slug === teamSlug ? (
                    <IconCheckmark />
                  ) : (
                    <TeamAvatar name={candidate.name} id={candidate.id} image={candidate.avatar} size={16} className="rounded" />
                  )
                }
              >
                {candidate.name}
              </DropdownMenu.Item>
            ))}
            <DropdownMenu.Item asChild icon={<IconAdd />}>
              <Link href="/onboarding?new=1">Create a team</Link>
            </DropdownMenu.Item>
          </>
        )}
        {size === 'lg' && (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Label className="truncate text-[13px]">{me?.user.email}</DropdownMenu.Label>
            <AccountMenuItems />
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

export default TeamMenu
